import { Box, Typography, IconButton } from '@mui/material';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { snowUrl } from 'service/config';
import { memo, useCallback, useState, useMemo, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DetailedAlerts from '../AlertDetails/DetailedAlerts';
import { useColumnFilters } from 'components/ColumnFilters/useColumnFilters';
import type { FilterOperator, SortModel } from 'components/ColumnFilters/types';
import {
  ColumnFilterPayloadItem,
  columnFilterPayloadToModel,
  mergeColumnFilterPayload,
  toColumnFiltersPayload,
  withColumnFilterOperators,
} from '../columnFilterUtils';

const EWS_FREE_TEXT_COLUMNS = ['number', 'description', 'incident', 'sys_created_by', 'sys_updated_by', 'category', 'classification'];
const TEXT_SEARCH_OPERATORS: FilterOperator[] = ['startswith', 'contains', 'notcontains'];

const formatAgeDuration = (diffMs: number): string => {
  if (Number.isNaN(diffMs) || diffMs < 0) return '-';

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

const formatAge = (initialEventTime: any): string => {
  const startTime = initialEventTime ? new Date(initialEventTime).getTime() : NaN;
  return formatAgeDuration(Date.now() - startTime);
};


const AGE_NUMBER_OPERATORS: FilterOperator[] = ['equals', 'gt', 'lt', 'between'];
const DAY_MS = 24 * 60 * 60 * 1000;
const AGE_TIMESTAMP_MIN = 1e10;

const toAgeDayFilter = (filter: ColumnFilterPayloadItem): ColumnFilterPayloadItem => {
  const now = Date.now();
  const [first, second] = filter.values.map(Number);
  const oldestOfDay = (days: number) => now - (days + 1) * DAY_MS + 1;
  const newestOfDay = (days: number) => now - days * DAY_MS;

  if (filter.operator === 'equals') return { ...filter, operator: 'between', values: [oldestOfDay(first), newestOfDay(first)] };
  if (filter.operator === 'gt') return { ...filter, operator: 'lte', values: [now - (first + 1) * DAY_MS] };
  if (filter.operator === 'lt') return { ...filter, operator: 'gt', values: [newestOfDay(first)] };
  if (filter.operator === 'between') {
    const [from, to] = [Math.min(first, second), Math.max(first, second)];
    return { ...filter, values: [oldestOfDay(to), newestOfDay(from)] };
  }
  return filter;
};
import DoneIcon from '@mui/icons-material/Done';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { createComments, getDuplicateAlerts, getSopMetrics } from 'service/unifiedAlerts';
import CircularProgress from '@mui/material/CircularProgress';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { GroupedTableFormatter } from 'components/Common/GroupedTableFormatter';
import GroupByShowMore from 'components/Common/GroupByShowMore';
import { useTheme } from "@mui/material/styles";
import { ReactComponent as EditImgIcon } from '../../../assets/PepWise2.0/edit-img.svg';
import { AlertsContext } from '../AlertsContext';
import { PlayCircleFilledSharp } from '@mui/icons-material';

interface Alert {
  number: string;
  sys_id: string;
  source: string;
  mappedSeverity: string;
  severity: string;
  sn_priority_group: string;
  state: string;
  category: string;
  last_event_time: string;
  assignment_group?: string;
  sector?: string;
  program?: string;
  description?: string;
  comments?: string;
  sys_updated_on: string;
  initial_event_time?: string;
  inc_sys_id?: string;
  incident?: string;
  domain?: string;
  department?: string;
  businessUnit?: string;
  application?: string;
  sopId?: string;
  sopName?: string;
  sopLink?: string;
  type?: string;
  dataQuality?: boolean;
  vendor?: string;
  isDandAIAG?: boolean;
  dqErrorMessage?: string | null;
  duplicate_count?: number;
  is_duplicate?: boolean;
}

interface AlertsTableProps {
  alerts: Alert[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  filterOptions: any;
  onPageSizeChange: (pageSize: number) => void;
  onSorting: (sortInfo: { sortBy: string; sortDir: string }) => void;
  sortingInfo: { sortBy: string; sortDir: string };
  isLoading: boolean;
  title: string | undefined;
  onGroupByChange?: (groupBy: string) => void;
  groupByList?: { label: string; value: string }[];
  groupBy?: string;
  includeGroupByMenuColumns?: string[];
  onGroupToggle?: (row: any) => void;
  onShowMoreClick?: (row: any) => void;
  loadingGroupId?: string | null;
  isDataOpsAdmin?: boolean;
  onManageSop?: () => void;
  idleFilterIcon?: string;
  columnFilters?: ColumnFilterPayloadItem[];
  onColumnFilterChange?: (payload: ColumnFilterPayloadItem[], sortModel: SortModel) => void;
  getColumnFilterValues?: (field: string) => Promise<any[]>;
}

const SeverityChip = ({ row }: { row: Alert }) => {
  const val = row.severity;

  const colorMap: Record<string, { bg: string; color: string }> = {
    Critical: { bg: '#f54b3c', color: '#fff' },
    Major: { bg: '#f79227', color: '#fff' },
    Minor: { bg: '#f1aa2c', color: '#fff' },
    Warning: { bg: '#facc32', color: '#fff' },
    OK: { bg: '#37b66e', color: '#fff' },
  };

  const style = colorMap[row.severity] ?? { bg: '#9E9E9E', color: '#fff' };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '28px',
        borderRadius: '4px',
        width: '100%',
        justifyContent: 'center',
      }}
    >
      {val}
    </Box>
  );
};

const PriorityChip = ({ row }: { row: Alert }) => {
  const val = row.sn_priority_group;

  const colorMap: Record<string, { bg: string; color: string }> = {
    Urgent: { bg: '#f54b3c', color: '#fff' },
    High: { bg: '#f79227', color: '#fff' },
    Moderate: { bg: '#f1aa2c', color: '#fff' },
    Low: { bg: '#facc32', color: '#fff' },
  };

  const style = colorMap[val] ?? { bg: '#E0E0E0', color: '#000' };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '28px',
        borderRadius: '4px',
        width: '100%',
        justifyContent: 'center',
      }}
    >
      {val}
    </Box>
  );
};

const defaultColumns = ['ALERT ID', 'DESCRIPTION', 'SOURCE', 'SEVERITY', 'PRIORITY', 'STATUS', 'AGE', 'D&AI', 'VENDOR', 'INCIDENT', 'ASSIGNMENT GROUP', 'LAST UPDATED', 'DOMAIN','SECTOR','DEPARTMENT', 'PROGRAM',  'COMMENTS', 'ACTIVITY', 'ACTIONS'];

const autoColumnWidth = (min = 140, flex = 1, max = min * 1.5) =>
  `minmax(clamp(${min}px, ${min / 10}vw, ${max}px), ${flex}fr)`;

const renderSeverityFormatter = ({ row }: { row: Alert }) => (row as any)?.isParent || (row as any)?.isShowMoreRow ? null : <SeverityChip row={row} />;

const renderPriorityFormatter = ({ row }: { row: Alert }) => (row as any)?.isParent || (row as any)?.isShowMoreRow ? null : row.sn_priority_group ? <PriorityChip row={row} /> : 'NA';

// Last updated time (ISO string or epoch ms), shared by the LAST UPDATED cell and its column filter options
const formatUpdatedTime = (value: any): string => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value ?? '') : d.toLocaleString('en-US');
};

const renderTimeFormatter = ({ row }: { row: Alert }) => {
  if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
  return <span>{formatUpdatedTime(row.sys_updated_on)}</span>;
};

const renderInitialTimeFormatter = ({ row }: { row: Alert }) => {
  if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
  const d = new Date(row.initial_event_time ?? '');
  return (
    <span>
      {Number.isNaN(d.getTime())
        ? row.initial_event_time
        : d.toLocaleString('en-US')}
    </span>
  );
};

const renderIncidentIDFormatter = ({ row }: { row: Alert }) => {
  if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
  return (
    <Typography
      variant='tableRowFont'
      sx={{
        color: "#39ACE7 !important",
        cursor: "pointer",
        textDecoration: row.inc_sys_id ? "underline" : "none",
      }}
      onClick={row.inc_sys_id ? () => window.open(`${snowUrl}/now/sow/record/incident/${row.inc_sys_id}`, "_blank") : undefined}
    >
      {row.incident || 'NA'}
    </Typography>
  );
};
const buildReverseSectorMap = (
  sectorMap: Record<string, string[] | string> = {}
): Record<string, string> => {
  const reverseMap: Record<string, string> = {};

  Object.entries(sectorMap).forEach(([parent, values]) => {
    if (Array.isArray(values)) {
      values.forEach((val) => {
        reverseMap[val] = parent;
      });
    } else {
      reverseMap[values] = parent;
      reverseMap[parent] = parent;
    }
  });

  return reverseMap;
};

const isEmptyValue = (value: any) => {
  if (value === null || value === undefined) return true;

  if (typeof value === "string") return value.trim() === "";

  if (Array.isArray(value)) return value.length === 0;

  return false;
};

const getRowsForColumnCheck = (rows: Alert[]) => {
  return rows.flatMap((row) => {
    const groupedRow = row as any;

    if (groupedRow?.isShowMoreRow) return [];

    if (groupedRow?.isParent) {
      return Array.isArray(groupedRow.children) ? groupedRow.children : [];
    }

    if (Array.isArray(groupedRow?.children)) {
      return groupedRow.children;
    }

    return [row];
  });
};

const hasAnyValue = (rows: any[], key: string) => {
  return rows.some((row) => !isEmptyValue(row?.[key]));
};

const AlertsTable = memo(({
  alerts,
  total,
  page,
  pageSize,
  onPageChange,
  filterOptions,
  onPageSizeChange,
  onSorting,
  sortingInfo,
  isLoading,
  title,
  onGroupByChange,
  groupByList = [],
  groupBy = 'none',
  includeGroupByMenuColumns = [],
  onGroupToggle,
  onShowMoreClick,
  loadingGroupId,
  isDataOpsAdmin,
  onManageSop,
  idleFilterIcon,
  columnFilters = [],
  onColumnFilterChange,
  getColumnFilterValues,
}: AlertsTableProps) => {

  const theme = useTheme();
  const colors = theme.palette;
  const navigate = useNavigate();
  const [isAlertsDrawerOpen, setIsAlertsDrawerOpen] = useState(false);
  const [entireRow, setEntireRow] = useState<Alert | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [commentErrors, setCommentErrors] = useState<Record<string, string>>({});
  const [statusMap, setStatusMap] = useState<
    Record<string, 'idle' | 'loading' | 'success' | 'error'>
  >({});
  const [expandedDuplicateAlerts, setExpandedDuplicateAlerts] = useState<Record<string, boolean>>({});
  const [duplicateChildren, setDuplicateChildren] = useState<Record<string, Alert[]>>({});
  const [loadingDuplicateId, setLoadingDuplicateId] = useState<string | null>(null);
  const [userPendingCount, setUserPendingCount] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;
    const fetchUserPendingCount = async () => {
      try {
        const res = await getSopMetrics('EWS');
        const count = res?.data?.userPendingCount ?? res?.userPendingCount ?? 0;
        if (isMounted && typeof count === 'number') {
          setUserPendingCount(count);
        }
      } catch (err) {
        console.error('Error fetching user pending count:', err);
      }
    };

    if (onManageSop && isDataOpsAdmin) {
      fetchUserPendingCount();
    }

    const interval = setInterval(() => {
      if (onManageSop && isDataOpsAdmin) {
        fetchUserPendingCount();
      }
    }, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [onManageSop, isDataOpsAdmin]);

  const alertsContext = useContext(AlertsContext);
  const alertCountView = alertsContext?.alertCountView ?? 'overall';

  const p = theme.palette;


  const handleDuplicateToggle = async (row: Alert) => {
    const alertId = row.number;

    if (expandedDuplicateAlerts[alertId]) {
      setExpandedDuplicateAlerts((prev) => ({
        ...prev,
        [alertId]: false,
      }));
      return;
    }

    if (duplicateChildren[alertId]) {
      setExpandedDuplicateAlerts((prev) => ({
        ...prev,
        [alertId]: true,
      }));
      return;
    }

    setLoadingDuplicateId(alertId);

    try {
      const response = await getDuplicateAlerts(alertId);
      const children = response?.records ?? [];

      setDuplicateChildren((prev) => ({
        ...prev,
        [alertId]: children,
      }));

      setExpandedDuplicateAlerts((prev) => ({
        ...prev,
        [alertId]: true,
      }));
    } catch (error) {
      console.error('Error loading duplicate alerts:', error);
    } finally {
      setLoadingDuplicateId(null);
    }
  };

  const validateComment = (alertId: string, value: string) => {
    if (value.length > 500) {
      setCommentErrors((prev) => ({
        ...prev,
        [alertId]: `Maximum ${500} characters allowed`,
      }));
      return false;
    }

    setCommentErrors((prev) => {
      const next = { ...prev };
      delete next[alertId];
      return next;
    });

    return true;
  };


  const handleAlertsClick = ({ row }: { row: Alert }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    const handleViewDetailsClick = (row: Alert) => {
      setEntireRow(row);
      setIsAlertsDrawerOpen(true);
    };
    return (
      <Typography
        variant='tableRowFont'
        sx={{
          color: "dodgerblue !important",
          cursor: "pointer",
        }}
        onClick={() => handleViewDetailsClick(row)}
      >
        VIEW DETAILS
      </Typography>
    );
  };


  const handleCommentChange = (alertId: string, value: string) => {
    setComments((prev) => ({
      ...prev,
      [alertId]: value,
    }));

    validateComment(alertId, value);
  };

  const handleCommentSubmit = async (row: Alert) => {
    const comment = comments[row.number] ?? '';

    // 🚫 block submission if invalid
    if (!validateComment(row.number, comment)) {
      return;
    }
    const payload = {
      alertId: row.number,
      comments: comment,
    };

    // 👉 set loading
    setStatusMap((prev) => ({
      ...prev,
      [row.number]: 'loading',
    }));

    try {
      const resp = await createComments(payload);

      if (resp === 'Comments Added Successfully') {
        setStatusMap((prev) => ({
          ...prev,
          [row.number]: 'success',
        }));
      } else {
        throw new Error('Failed');
      }
    } catch (e) {
      console.info(JSON.stringify(e))
      setStatusMap((prev) => ({
        ...prev,
        [row.number]: 'error',
      }));
    }

    setTimeout(() => {
      setStatusMap((prev) => ({
        ...prev,
        [row.number]: 'idle',
      }));
    }, 2000);

    setEditingRow(null);
  };

  const renderCommentFormatter = ({ row }: { row: Alert }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    const value = comments[row.number] ?? row.comments ?? '';
    const isEditing = editingRow === row.number;
    const status = statusMap[row.number] || 'idle';
    const error = commentErrors[row.number];

    let actionElement: React.ReactNode;


    if (error) {
      actionElement = (
        <IconButton
          size="small"
          disabled={!error}
          sx={{ p: 0.5 }}
        >
          <Tooltip title={error} arrow>
            <ErrorOutlineIcon
              fontSize='small'
              sx={{
                color: '#ef4444',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </IconButton>
      )
    }
    else if (!error && status === 'loading') {
      actionElement = <CircularProgress size={16} />;
    } else if (!error && status === 'success') {
      actionElement = <Box sx={{ fontSize: '14px' }}>✅</Box>;
    } else if (!error && status === 'error') {
      actionElement = <Box sx={{ fontSize: '14px' }}>❌</Box>;
    } else if (!error && isEditing) {
      actionElement = (
        <IconButton
          size="small"
          disabled={!!error}
          onMouseDown={(e) => {
            e.preventDefault(); // important to prevent blur
            handleCommentSubmit(row);
          }}
          sx={{ p: 0.5 }}
        >
          <DoneIcon fontSize="small" />
        </IconButton>
      );
    } else {
      actionElement = (
        <IconButton
          size="small"
          onClick={() => setEditingRow(row.number)}
          sx={{ p: 0.5 }}
        >
          <EditImgIcon style={{ width: 18, height: 18 }} />
        </IconButton>
      );
    }

    return (
      <Box
        sx={{
          width: 220,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
        onClick={() => !isEditing && setEditingRow(row.number)}
      >
        <InputBase
          value={value}
          readOnly={!isEditing}
          onChange={(e) =>
            handleCommentChange(row.number, e.target.value)
          }
          onBlur={() => {
            if (isEditing) {
              setEditingRow(null);
            }
          }}
          placeholder="Add Comment..."
          sx={{
            flex: 1,
            fontSize: '0.85rem',
            px: 1,
            py: 0.25,
            borderRadius: '4px',
            backgroundColor: isEditing ? '#f5f7fb' : 'transparent',
            border: error ? '1px solid #ef4444' : '1px solid transparent', // 🔴 highlight
            '& .MuiInputBase-input': {
              color: '#000',
              padding: 0,
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#6b7280',
              opacity: 1,
            },
          }}
        />
        {actionElement}
      </Box>
    );
  };

  const renderAgeFormatter = ({ row }: { row: Alert }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    return formatAge(row?.initial_event_time);
  };

  const renderAreaFormatter = ({ row }: { row: Alert }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    return (
      <Typography
        variant='tableRowFont'
      >
        {row?.isDandAIAG === true ? 'YES' : 'No'}
      </Typography>
    )
  };


  const renderButtons = ({ row }: { row: Alert }) => {

    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    const isSopLinked = row?.sopId;
    const playActionElement = (
      <Tooltip title="Self Heal">
        <IconButton size="small" sx={{ p: 0.5, color: colors.buttons.primary.disabledBg }}>
          <PlayCircleFilledSharp fontSize="small" />
        </IconButton>
      </Tooltip>
    );

    const descriptionActionElement = (
      <Tooltip title={isSopLinked ? 'SOP Linked' : 'SOP Not Linked'} arrow>
        <IconButton
          size="small"
          sx={{
            color: isSopLinked
                      ? colors.buttons.primary.color
                      : colors.buttons.primary.disabledBg,
            p: 0.5,
            cursor: isSopLinked ? 'pointer' : 'default'
          }}
          onClick={() => {
            if (row.sopLink) {
              window.open(row.sopLink, '_blank');
            }
          }}
        >
          <DescriptionRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {playActionElement}
        {descriptionActionElement}
      </Box>
    );
  };

  const getMappedSector = (value: any, reverseMap: any) => {
    if (Array.isArray(value)) {
      return value.map(v => reverseMap[v] || v).join(", ");
    }

    return reverseMap[value] || value;
  };

  const reverseSectorMap = useMemo(() => {
    return buildReverseSectorMap(
      filterOptions?.sectorDropdownValues ?? {}
    );
  }, [filterOptions]);

  const renderSectorFormatter = ({ row }: { row: Alert }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
    const mapedSector = getMappedSector(row.sector, reverseSectorMap)
    return (
      <Typography
        variant='tableRowFont'
      >
        {mapedSector}
      </Typography>
    )
  };

  const renderAlertIDFormatter = ({ row }: { row: Alert }) => {
    if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;

    const hasDuplicates =
      alertCountView === 'unique' &&
      row?.is_duplicate === false &&
      (row?.duplicate_count ?? 0) > 0;

    const isExpanded = expandedDuplicateAlerts[row.number];
    const isDuplicateLoading = loadingDuplicateId === row.number;
    const isDuplicateChild = (row as any)?.isDuplicateChild;

    let duplicateExpandIcon;

    if (isDuplicateLoading) {
      duplicateExpandIcon = (
        <CircularProgress
          size={16}
          sx={{ color: colors.table.expandIcon }}
        />
      );
    } else if (isExpanded) {
      duplicateExpandIcon = (
        <RemoveIcon
          fontSize="small"
          sx={{ color: colors.table.expandIcon }}
        />
      );
    } else {
      duplicateExpandIcon = (
        <AddIcon
          fontSize="small"
          sx={{ color: colors.table.expandIcon }}
        />
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: isDuplicateChild ? 4 : 0,
        }}
      >
        {hasDuplicates && (
          <IconButton
            size="small"
            disabled={isDuplicateLoading}
            onClick={(event) => {
              event.stopPropagation();
              handleDuplicateToggle(row);
            }}
            sx={{
              p: 0,
              width: 20,
              height: 20,
            }}
          >
            {duplicateExpandIcon}
          </IconButton>
        )}

        {isDuplicateChild && (
          <Box
            sx={{
              width: 12,
              height: 1,
              backgroundColor: '#9CA3AF',
              flexShrink: 0,
            }}
          />
        )}

        <Typography
          variant='tableRowFont'
          sx={{
            color: "#39ACE7 !important",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => window.open(`${snowUrl}/now/sow/record/em_alert/${row.sys_id}`, "_blank")}
        >
          {row.number}
        </Typography>

        {hasDuplicates && (
          <Typography
            variant="body2"
            sx={{
              color: colors.widget.text.duplicateCount,
              fontWeight: 500,
              ml: 2,
              whiteSpace: 'nowrap',
            }}
          >
            + {row.duplicate_count}
          </Typography>
        )}
      </Box>
    );
  };

  const getGroupedCellDisplayValue = useCallback((row: any, groupByValue: string) => {
    const value = row?.[groupByValue] ?? row?.parentGroupValue ?? row?.groupValue ?? row?.key ?? row?.value ?? row?.name ?? 'Unassigned';

    if (groupByValue === 'sector') {
      return getMappedSector(value, reverseSectorMap) || 'Unassigned';
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    return value || 'Unassigned';
  }, [reverseSectorMap]);

  const getColumnsWithGrouping = useCallback((baseColumns: any[], groupByValue: string) => {
    if (!groupByValue || groupByValue === 'none') {
      return baseColumns;
    }

    const groupedColumnIndex = baseColumns.findIndex(col => col.key === groupByValue);
    const groupedColumn = groupedColumnIndex !== -1 ? baseColumns[groupedColumnIndex] : null;

    const groupedFirstColumn = {
      key: groupByValue,
      name: groupedColumn ? groupedColumn.name : groupByValue.toUpperCase().replace(/_/g, ' '),
      minWidth: 250,
      maxWidth: 750,
      width: autoColumnWidth(250, 2),
      formatter: ({ row }: any) => {
        if (row.isShowMoreRow) {
          const groupValue = row.parentRow?.[groupByValue] ?? row?.[groupByValue] ?? row?.parentRow?.groupValue ?? row?.parentGroupValue;
          const isGroupLoading = loadingGroupId === `${groupByValue}-${groupValue}`;
          return (
            <GroupByShowMore
              row={row}
              handleShowMoreClick={onShowMoreClick || (() => undefined)}
              isLoading={isGroupLoading}
            />
          );
        }

        return (
          <GroupedTableFormatter
            row={row}
            groupByColumn={groupByValue}
            groupByLabel={getGroupedCellDisplayValue(row, groupByValue)}
            onToggle={onGroupToggle || (() => undefined)}
            countLabel='Alerts'
          />
        );
      },
      resizable: true,
      flexGrow: 2,
      sortable: true
    };

    let remainingColumns = [...baseColumns];
    if (groupedColumnIndex !== -1) {
      remainingColumns = baseColumns.filter((_, index) => index !== groupedColumnIndex);
    }

    remainingColumns = remainingColumns.map((column: any) => {
      const originalFormatter = column.formatter;
      const originalRenderCell = column.renderCell;

      return {
        ...column,
        renderCell: undefined,
        formatter: (props: any) => {
          const row = props?.row;

          if (row?.isParent || row?.isShowMoreRow) {
            return null;
          }

          if (originalFormatter) {
            return originalFormatter(props);
          }

          if (originalRenderCell) {
            return originalRenderCell(props);
          }

          const value = row?.[column.key];
          return Array.isArray(value) ? value.join(', ') : value ?? '';
        }
      };
    });

    return [groupedFirstColumn, ...remainingColumns];
  }, [getGroupedCellDisplayValue, loadingGroupId, onGroupToggle, onShowMoreClick]);
['STATUS', 'AGE', 'D&AI', 'VENDOR','INCIDENT', 'ASSIGNMENT GROUP', 'LAST UPDATED', 'DOMAIN','SECTOR','DEPARTMENT', 'PROGRAM',  'COMMENTS', 'ACTIVITY', 'ACTIONS'];

  const columns = useMemo(() => {
    const allColumns = [
      { key: 'number', name: 'ALERT ID', width: autoColumnWidth(150), formatter: renderAlertIDFormatter },
      {
        key: 'description', name: 'DESCRIPTION', width: autoColumnWidth(200, 2),
        formatter: ({ row }: { row: Alert }) => {
          const description = row.description ?? '';
          return (
            <span title={description}>
              {description.length > 50 ? `${description.slice(0, 50)}...` : description}
            </span>
          );
        }
      },
      { key: 'source', name: 'SOURCE', width: autoColumnWidth(110) },
      {
        key: 'mappedSeverity',
        name: 'SEVERITY',
        width: autoColumnWidth(110),
        cellClass: 'alerts-badge-cell',
        renderCell: SeverityChip,
        formatter: renderSeverityFormatter,
      },
      {
        key: 'sn_priority_group',
        name: 'PRIORITY',
        width: autoColumnWidth(110),
        cellClass: 'alerts-badge-cell',
        renderCell: PriorityChip,
        formatter: renderPriorityFormatter,
      },
      { key: 'state', name: 'STATUS', width: autoColumnWidth(110) },
      {
        key: 'age',
        name: 'AGE',
        width: autoColumnWidth(120),
        formatter: renderAgeFormatter,
        formatFilterValue: formatAge,
      },
      {
        key: 'isDandAIAG',
        name: 'D&AI',
        width: autoColumnWidth(120),
        formatter: renderAreaFormatter,
        isFilterOptionDisabled: (optionValue: string, selectedValues: string[]) =>
          selectedValues.length > 0 && !selectedValues.includes(optionValue),
      },
      {
        key: 'vendor',
        name: 'VENDOR',
        width: autoColumnWidth(120)
      },
      { key: 'incident', name: 'INCIDENT', width: autoColumnWidth(110), formatter: renderIncidentIDFormatter },
      { key: 'assignment_group', name: 'ASSIGNMENT GROUP', width: autoColumnWidth(180) },
      {
        key: 'sys_updated_on',
        name: 'LAST UPDATED',
        width: autoColumnWidth(160),
        formatter: renderTimeFormatter,
      },
      { key: 'domain', name: 'DOMAIN', width: autoColumnWidth(110) },
      {
        key: 'sector',
        name: 'SECTOR',
        width: autoColumnWidth(110),
        formatter: renderSectorFormatter,
      },
      { key: 'department', name: 'DEPARTMENT', width: autoColumnWidth(150) },
      { key: 'program', name: 'PROGRAM', width: autoColumnWidth(150) },
      { key: 'type', name: 'TYPE', width: autoColumnWidth(110) },
      {
        key: 'dataQuality',
        name: 'DATA QUALITY',
        width: autoColumnWidth(130),
        formatter: ({ row }: { row: Alert }) => {
          if ((row as any)?.isParent || (row as any)?.isShowMoreRow) return null;
          return row.dataQuality ? 'True' : 'False';
        }
      },
      {
        key: 'comments',
        name: 'COMMENTS',
        width: autoColumnWidth(160),
        formatter: renderCommentFormatter,
      },
      {
        key: 'activity',
        name: 'ACTIVITY',
        width: autoColumnWidth(80),
        formatter: renderButtons,
      },
      { key: 'actions', name: 'ACTIONS', width: autoColumnWidth(150), formatter: handleAlertsClick },
      { key: 'category', name: 'CATEGORY', width: autoColumnWidth(140) },
      { key: 'classification', name: 'CLASSIFICATION', width: autoColumnWidth(160) },
      { key: 'cmdb_ci', name: 'CI', width: autoColumnWidth(110) },
      { key: 'sys_created_by', name: 'CREATED BY', width: autoColumnWidth(150) },
      { key: 'sys_updated_by', name: 'UPDATED BY', width: autoColumnWidth(150) },
      { key: 'application', name: 'APPLICATION', width: autoColumnWidth(150) },
      { key: 'businessUnit', name: 'BUSINESS UNIT', width: autoColumnWidth(150) },
      { key: 'capability', name: 'CAPABILITY', width: autoColumnWidth(150) },
    ];

    const rowsForColumnCheck = getRowsForColumnCheck(alerts);

    const alwaysShow = ['number', 'description', 'source', 'mappedSeverity', 'sn_priority_group', 'state', 'type', 'comments', 'activity', 'actions', 'incident', 'assignment_group', 'dataQuality', 'sys_updated_on', 'cmdb_ci','domain','sector','department','program', 'isDandAIAG'];

    const baseColumns = allColumns.filter((col) => {

      if (alwaysShow.includes(col.key)) return true;

      if (groupBy && groupBy !== 'none' && col.key === groupBy) return true;


      // real check
      return hasAnyValue(rowsForColumnCheck, col.key);
    });

    if (groupBy && groupBy !== 'none') {
      return getColumnsWithGrouping(baseColumns, groupBy);
    }

    return baseColumns;

  }, [alerts, comments, editingRow, statusMap, commentErrors, groupBy, getColumnsWithGrouping, alertCountView, expandedDuplicateAlerts, loadingDuplicateId]);

  const tableDataWithDuplicates = useMemo(() => {
    const rows: any[] = [];

    alerts.forEach((row: any) => {
      rows.push(row);

      if (
        alertCountView === 'unique' &&
        row?.number &&
        expandedDuplicateAlerts[row.number] &&
        duplicateChildren[row.number]?.length > 0
      ) {
        const children = duplicateChildren[row.number].map(
          (child: Alert, index: number) => ({
            ...child,
            isDuplicateChild: true,
            duplicateParentId: row.number,
            duplicateChildIndex: index,
            parentGroupValue: row?.parentGroupValue,
          })
        );

        rows.push(...children);
      }
    });

    return rows;
  }, [alerts, expandedDuplicateAlerts, duplicateChildren, alertCountView]);

  const filterConfiguredColumns = useMemo(
    () => withColumnFilterOperators(columns, EWS_FREE_TEXT_COLUMNS)
      .map((column) => (column.key === 'age' ? { ...column, columnDetails: { age: 'number' }, showValueList: false, filterOperators: AGE_NUMBER_OPERATORS } : column))
      .map((column) => (column.key === 'description' ? { ...column, showValueList: false, filterOperators: TEXT_SEARCH_OPERATORS } : column))
      .map((column) => (['number', 'incident', 'sys_updated_on'].includes(column.key) ? { ...column, showValueList: false } : column)),
    [columns]
  );

  const getColumnFilterOptions = useCallback(
    async (field: string): Promise<any[]> => (await getColumnFilterValues?.(field)) ?? [],
    [getColumnFilterValues]
  );

  const columnFilterController = useColumnFilters({
    columns: filterConfiguredColumns,
    rows: tableDataWithDuplicates,
    mode: 'server',
    initialFilterModel: columnFilterPayloadToModel(columnFilters),
    onChange: ({ filterModel, sortModel, changedColumnKey }) => {
      const payload = mergeColumnFilterPayload(columnFilters, toColumnFiltersPayload(filterModel), changedColumnKey);
      const expandedPayload = payload.map((filter) => {

        if (filter.id === 'age' && filter.values.length > 0 && filter.values.every((value) => Number(value) < AGE_TIMESTAMP_MIN)) {
          return toAgeDayFilter(filter);
        }
        return filter;
      });
      onColumnFilterChange?.(expandedPayload, sortModel);
    },
  });
  const isColumnFilterEnabled = Boolean(onColumnFilterChange);

  return (
    <Box
      sx={{
        position: 'relative',
        '& .rdg-cell': {
          borderInlineEnd: '1px solid #fff !important',
          borderBlockEnd: '1px solid #fff !important',
          paddingTop: '16px !important',
          paddingBottom: '16px !important',
        },
        '& .alerts-badge-cell': {
          display: 'flex !important',
          alignItems: 'center !important',
          paddingLeft: '20px !important',
          paddingRight: '20px !important',
          paddingInline: '20px !important',
        },
        '& .rdg-header-row .rdg-cell': {
          borderInlineEnd: '1px solid #fff !important',
          borderBlockEnd: '1px solid #fff !important',
          display: 'flex !important',
          alignItems: 'center !important',
        },
        '& .rdg-header-row .rdg-cell > div': {
          width: '100% !important',
        },
        '& .rdg-row': {
          borderBlockEnd: '1px solid #fff !important',
        },
      }}
    >

      <Box sx={{ position: 'absolute', top: 4, left: 0, right: 0, display: 'flex', alignItems: 'center', zIndex: 10, pointerEvents: 'none' }}>
        <Typography variant="h4" sx={{ color: p.primary.main, fontWeight: 500, pointerEvents: 'auto' }}>
          {title}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {isDataOpsAdmin && (
          <Box
            component="button"
            onClick={() => navigate('/dataops/early-warning-rule')}
            sx={{
              backgroundColor: colors.backgroundLight.secondary,
              border: `1px solid ${colors.buttons.primary.borderedBorder}`,
              color: colors.buttons.primary.color,
              px: 2.5,
              height: 32,
              mr: onManageSop ? 2 : '380px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              textTransform: 'uppercase',
              pointerEvents: 'auto',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              '&:hover': {
                backgroundColor: colors.backgroundLight.primary,
                borderColor: colors.buttons.primary.color,
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(16, 55, 113, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: '0 1px 2px rgba(16, 55, 113, 0.1)',
              }
            }}
          >
            EWS RULE ENGINE
          </Box>
        )}
        {onManageSop && isDataOpsAdmin && (
          <Box
            component="button"
            onClick={onManageSop}
            sx={{
                backgroundColor: colors.backgroundLight.secondary,
                border: `1px solid ${colors.buttons.primary.borderedBorder}`,
                color: colors.buttons.primary.color,
                px: 2.5,
                height: 32,
                mr: '16px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                textTransform: 'uppercase',
                pointerEvents: 'auto',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: colors.backgroundLight.primary,
                  borderColor: colors.buttons.primary.color,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(16, 55, 113, 0.15)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 1px 2px rgba(16, 55, 113, 0.1)',
                }
              }}
          >
            Update SOP
          </Box>
        )}
        {onManageSop && isDataOpsAdmin && (
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              mr: '380px',
              pointerEvents: 'auto',
            }}
          >
            <Box
              component="button"
              onClick={() => navigate('/dataops/manage-sop?type=EWS')}
              sx={{
                backgroundColor: colors.backgroundLight.secondary,
                border: `1px solid ${colors.buttons.primary.borderedBorder}`,
                color: colors.buttons.primary.color,
                px: 2.5,
                height: 32,
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                textTransform: 'uppercase',
                pointerEvents: 'auto',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: colors.backgroundLight.primary,
                  borderColor: colors.buttons.primary.color,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(16, 55, 113, 0.15)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: '0 1px 2px rgba(16, 55, 113, 0.1)',
                }
              }}
            >
              MANAGE SOP
            </Box>
            {userPendingCount > 0 && (
              <Typography
                onClick={() => navigate('/dataops/manage-sop?type=EWS')}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  pt: '2px',
                  color: '#DC2626',
                  fontSize: '11px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                {userPendingCount} {userPendingCount === 1 ? 'approval' : 'approvals'} pending
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <CustomizedDataGrid
        key="snow-unified-alerts-grid"
        toolbarSx={{ mb: '10px' }}
        tableColumns={filterConfiguredColumns}
        tableData={tableDataWithDuplicates}
        tableType="UnifiedAlertsSnow"
        rowHeight={45}
        defaultColumnNames={defaultColumns}
        listLength={total}
        page={page}
        pageSize={pageSize}
        paginationData={{ totalElements: total, totalPages: Math.ceil(total / pageSize) }}
        onSorting={onSorting}
        currSorting={sortingInfo}
        handlePageChange={(_: any, p: number) => onPageChange(p)}
        handlepageSizeChange={(e: any) => onPageSizeChange(Number.parseInt(e.target.value))}
        isFetching={isLoading}
        isDateRangeFilter={false}
        isDisabled={false}
        isShowMoreButton={true}
        pageSizeOptions={[10, 50, 100, 200, 500, 1000]}
        isPrevNextPagination
        isRenderPagination={total > 0}
        isGroupBy={true}
        onGroupByChange={onGroupByChange}
        groupBy={groupBy}
        groupByList={groupByList}
        includeGroupByMenuColumns={includeGroupByMenuColumns}
        isSortIcon={!idleFilterIcon}
        idleFilterIcon={idleFilterIcon}
        style={{minHeight: "130px"}}
        enableColumnFilter={isColumnFilterEnabled}
        columnFilterController={isColumnFilterEnabled ? columnFilterController : undefined}
        getColumnFilterValues={getColumnFilterValues ? getColumnFilterOptions : undefined}
      />
      {total === 0 && (
        <Box sx={{ mt: 1, px: 1, py: 1, borderRadius: '4px', backgroundColor: `${theme.palette.table.pagination.BgColor} !important` }}>
          <Typography variant="bodyText" color={`${theme.palette.table.pagination.text} !important`}>
            Showing 0 - 0 of 0 results
          </Typography>
        </Box>
      )}

      <DetailedAlerts
        open={isAlertsDrawerOpen}
        onClose={() => setIsAlertsDrawerOpen(false)}
        onOpen={() => setIsAlertsDrawerOpen(true)}
        entireRow={entireRow}
      />
    </Box>
  );
});

export default AlertsTable;